import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-54">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-55">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-56" />

          {/* Modal Content */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative w-full max-w-2xl max-h-[80vh] bg-gradient-to-b from-navy-800 to-navy-900 border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden z-10" data-id="element-57">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-navy-800/95 backdrop-blur-sm z-10" data-id="element-58">
              <h3 className="text-xl font-bold text-white" data-id="element-59">{title}</h3>
              <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-gold-500" aria-label="Cerrar" data-id="element-60">
                <XIcon className="w-5 h-5" data-id="element-61" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-slate-300 leading-relaxed space-y-4 text-sm" data-id="element-62">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-10 border-t border-white/5 bg-navy-900" data-id="element-63">
        <div className="container mx-auto px-4" data-id="element-64">
          <div className="flex flex-col items-center gap-4" data-id="element-65">
            {/* Links */}
            <div className="flex items-center gap-6" data-id="element-66">
              <button onClick={() => setActiveModal('privacy')} className="text-sm text-slate-500 hover:text-gold-500 transition-colors focus:outline-none focus:underline" data-id="element-67">
                Política de Privacidad
              </button>
              <span className="text-slate-700" data-id="element-68">|</span>
              <button onClick={() => setActiveModal('terms')} className="text-sm text-slate-500 hover:text-gold-500 transition-colors focus:outline-none focus:underline" data-id="element-69">
                Términos y Condiciones
              </button>
            </div>

            {/* Copyright */}
            <p className="text-sm text-slate-600" data-id="element-70">
              © {new Date().getFullYear()} Finanzas Sin Excusas. Todos los
              derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-71">
        <p className="text-slate-400 italic" data-id="element-72">
          Última actualización: marzo 2026
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-73">
          1. Información que recopilamos
        </h4>
        <p data-id="element-74">
          Al adquirir nuestro eBook, recopilamos únicamente la información
          necesaria para procesar tu compra, como tu nombre y dirección de
          correo electrónico. No recopilamos datos financieros directamente;
          estos son procesados de forma segura por nuestra plataforma de pago.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-75">
          2. Uso de la información
        </h4>
        <p data-id="element-76">Utilizamos tu información exclusivamente para:</p>
        <ul className="list-disc list-inside space-y-1 text-slate-400" data-id="element-77">
          <li data-id="element-78">Entregar el producto digital que adquiriste</li>
          <li data-id="element-79">Enviar confirmación de compra y recibos</li>
          <li data-id="element-80">
            Comunicarnos contigo en caso de ser necesario respecto a tu compra
          </li>
        </ul>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-81">
          3. Protección de datos
        </h4>
        <p data-id="element-82">
          Nos comprometemos a proteger tu información personal. Implementamos
          medidas de seguridad técnicas y organizativas para prevenir el acceso
          no autorizado, la pérdida o la alteración de tus datos.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-83">
          4. Compartir información
        </h4>
        <p data-id="element-84">
          No vendemos, intercambiamos ni transferimos tu información personal a
          terceros. La única excepción son los proveedores de servicios que nos
          ayudan a operar nuestro negocio (procesadores de pago), quienes están
          obligados a mantener la confidencialidad de tu información.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-85">5. Cookies</h4>
        <p data-id="element-86">
          Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
          navegación. Puedes configurar tu navegador para rechazar cookies,
          aunque esto podría afectar algunas funcionalidades del sitio.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-87">6. Tus derechos</h4>
        <p data-id="element-88">
          Tienes derecho a acceder, rectificar o eliminar tu información
          personal en cualquier momento. También puedes solicitar que dejemos de
          usar tus datos para fines de comunicación.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-89">
          7. Cambios a esta política
        </h4>
        <p data-id="element-90">
          Nos reservamos el derecho de actualizar esta política de privacidad en
          cualquier momento. Los cambios serán publicados en esta página con la
          fecha de actualización correspondiente.
        </p>
      </Modal>

      {/* Terms & Conditions Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-91">
        <p className="text-slate-400 italic" data-id="element-92">
          Última actualización: marzo 2026
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-93">
          1. Aceptación de los términos
        </h4>
        <p data-id="element-94">
          Al realizar una compra en este sitio, aceptas estos términos y
          condiciones en su totalidad. Si no estás de acuerdo con alguno de
          estos términos, te pedimos que no realices la compra.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-95">
          2. Producto digital
        </h4>
        <p data-id="element-96">
          "Finanzas Sin Excusas" es un producto digital (eBook) que se entrega
          en formato electrónico. Una vez completada la compra, recibirás acceso
          al material a través del correo electrónico proporcionado.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-97">
          3. Uso permitido
        </h4>
        <p data-id="element-98">El eBook es para uso personal únicamente. Queda prohibido:</p>
        <ul className="list-disc list-inside space-y-1 text-slate-400" data-id="element-99">
          <li data-id="element-100">
            Reproducir, distribuir o compartir el contenido total o parcialmente
          </li>
          <li data-id="element-101">Revender o redistribuir el producto</li>
          <li data-id="element-102">Utilizar el contenido con fines comerciales sin autorización</li>
        </ul>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-103">
          4. Propiedad intelectual
        </h4>
        <p data-id="element-104">
          Todo el contenido del eBook, incluyendo texto, gráficos y diseño, está
          protegido por derechos de autor. La compra del eBook te otorga una
          licencia personal e intransferible para su uso.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-105">
          5. Garantía de devolución
        </h4>
        <p data-id="element-106">
          Ofrecemos una garantía de devolución de 7 días. Si dentro de los
          primeros 7 días después de tu compra sientes que el producto no es
          para ti, puedes solicitar un reembolso completo. Para solicitar la
          devolución, debes comunicarte con nosotros dentro del plazo
          establecido.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-107">
          6. Limitación de responsabilidad
        </h4>
        <p data-id="element-108">
          El contenido del eBook es de carácter educativo e informativo. Los
          resultados pueden variar según la situación individual de cada
          persona. No garantizamos resultados financieros específicos. Este
          producto no constituye asesoría financiera profesional.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-109">
          7. Precio y pago
        </h4>
        <p data-id="element-110">
          El precio del producto está claramente indicado en la página de venta.
          Nos reservamos el derecho de modificar los precios en cualquier
          momento, sin que esto afecte las compras ya realizadas.
        </p>

        <h4 className="text-white font-bold text-base mt-4" data-id="element-111">
          8. Modificaciones
        </h4>
        <p data-id="element-112">
          Nos reservamos el derecho de modificar estos términos y condiciones en
          cualquier momento. Las modificaciones entrarán en vigor desde su
          publicación en este sitio.
        </p>
      </Modal>
    </>;
}